import { useQuery } from "@apollo/client";
import Button from "@/components/bricks/button/Button";
import { FormikProps } from "formik";
import { GET_PERSONS_BY_FIRSTNAMES_LASTNAME } from "../../../api/person/queries";
import { PersonByFirstNameLastNameResponse } from "../../autocomplete/interface";
import { FormikDatePicker } from "../../formcontrols/DatePicker";
import { TextInputFormik } from "../../formcontrols/InputItem";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../select/Select";
import { StickyFooter } from "../../stickyFooter/modalStickyFooter";
import DuplicatePersonWarning from "./DuplicatePersonWarning";
import {
  CreatePersonKodeDict,
  OpretPersonStep1Formik,
  OpretPersonStep2Formik,
} from "./useOpretPerson";
import IsRequired from "../../formcontrols/common/IsRequired";
import { SelectFormik } from "../../formcontrols/Select";
import { useEffect } from "react";

export default function Step1({
  setModalOpen,
  step1,
  step2,
  isKoenManuallyChanged,
  setIsKoenManuallyChanged,
}: {
  setModalOpen: (open: boolean) => void;
  step1: FormikProps<OpretPersonStep1Formik>;
  step2: FormikProps<OpretPersonStep2Formik>;
  setIsKoenManuallyChanged: (boolean: boolean) => void;
  isKoenManuallyChanged: boolean;
}) {
  const { values, setFieldValue } = step1;

  const {
    data,
    // loading
  } = useQuery<{
    personer: {
      edges: {
        node: PersonByFirstNameLastNameResponse;
      }[];
    };
  }>(
    GET_PERSONS_BY_FIRSTNAMES_LASTNAME({
      fornavne: step1.values.fornavne,
      efternavn: step1.values.efternavn,
    }),
    {
      variables: {
        fornavne: step1.values.fornavne,
        efternavn: step1.values.efternavn,
      },
      skip: !step1.values.fornavne || !step1.values.efternavn,
    },
  );
  useEffect(() => {
    const sanitizeCprNr = () => {
      let cprNr = step1.values.cprNr;
      cprNr = cprNr.replace(/\D/g, "");
      cprNr = cprNr.slice(0, 4);

      if (cprNr !== step1.values.cprNr) {
        step1.setFieldValue("cprNr", cprNr);
      }
    };
    sanitizeCprNr();
  }, [step1]);
  useEffect(() => {
    const cprNr = values.cprNr;
    if (cprNr.length === 4 && !isKoenManuallyChanged) {
      setFieldValue("koen", parseInt(cprNr) % 2 === 1 ? "M" : "K");
    }
  }, [values.cprNr, isKoenManuallyChanged, setFieldValue]);

  const personsWithSameName = data
    ? data.personer.edges.map((edge) => edge.node)
    : [];

  return (
    <form
      autoComplete="off"
      onSubmit={(e) => {
        step1.handleSubmit(e);
      }}
    >
      <div className="c-item-list c-modal-container-inner">
        <div className="flex-100">
          <Select
            value={step1.values.createPerson.kode.toString()}
            onValueChange={(value) => {
              switch (value) {
                case CreatePersonKodeDict.ovrig.kode.toString(): {
                  step1.setFieldValue(
                    "createPerson",
                    CreatePersonKodeDict.ovrig,
                    true,
                  );
                  break;
                }
                case CreatePersonKodeDict.advokatfuldmaegtig.kode.toString(): {
                  step1.setFieldValue(
                    "createPerson",
                    CreatePersonKodeDict.advokatfuldmaegtig,
                    true,
                  );
                  break;
                }
                case CreatePersonKodeDict.advokat.kode.toString(): {
                  step1.setFieldValue(
                    "createPerson",
                    CreatePersonKodeDict.advokat,
                    true,
                  );
                  break;
                }
                case CreatePersonKodeDict.euadvokat.kode.toString(): {
                  step1.setFieldValue(
                    "createPerson",
                    CreatePersonKodeDict.euadvokat,
                    true,
                  );
                  break;
                }
              }
            }}
          >
            <label className="form-label">
              {"Personart"}
              <IsRequired />
            </label>
            <SelectTrigger />
            <SelectContent>
              <SelectItem value={CreatePersonKodeDict.ovrig.kode.toString()}>
                {CreatePersonKodeDict.ovrig.beskrivelse}
              </SelectItem>
              <SelectItem
                value={CreatePersonKodeDict.advokatfuldmaegtig.kode.toString()}
              >
                {CreatePersonKodeDict.advokatfuldmaegtig.beskrivelse}
              </SelectItem>
              <SelectItem value={CreatePersonKodeDict.advokat.kode.toString()}>
                {CreatePersonKodeDict.advokat.beskrivelse}
              </SelectItem>
              <SelectItem
                value={CreatePersonKodeDict.euadvokat.kode.toString()}
              >
                {CreatePersonKodeDict.euadvokat.beskrivelse}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-100">
          <TextInputFormik
            config={{
              component: "textinput",
              id: "fornavne",
              label: "Fornavne",
              isRequired: true,
              initialValue: "",
            }}
            formik={step1}
          />
        </div>
        <div className="flex-100">
          <TextInputFormik
            config={{
              component: "textinput",
              id: "efternavn",
              label: "Efternavn",
              isRequired: true,
              initialValue: "",
            }}
            formik={step1}
          />
        </div>
        <div className="d-flex flex-row align-items-start gap-2">
          <div style={{ width: 160 }}>
            <FormikDatePicker
              formik={step1}
              config={{
                component: "datepicker",
                id: "foedselsDato",
                label: "Fødselsdato",
                initialValue: "",
                isRequired:
                  step1.values.createPerson.kode !==
                  CreatePersonKodeDict.ovrig.kode,
              }}
            />
          </div>
          <div>
            <div className="form-label">&nbsp;</div>
            <div
              style={{
                padding: "0.35rem 0",
              }}
            >
              -
            </div>
          </div>
          <div style={{ width: 100 }}>
            <TextInputFormik
              formik={step1}
              config={{
                component: "textinput",
                id: "cprNr",
                label: "CPR-nr.",
                placeholder: "0000",
                initialValue: "",
              }}
            />
          </div>
        </div>
        <div>
          <SelectFormik
            config={{
              component: "select",
              id: "koen",
              label: "Køn",
              placeholder: "",
            }}
            clearable
            data={[
              { label: "Mand", value: "M" },
              { label: "Kvinde", value: "K" },
              { label: "Nonbinær", value: "N" },
            ]}
            formik={step1}
            additionalOnChange={() => {
              setIsKoenManuallyChanged(true);
            }}
          />
        </div>
        <div>
          <TextInputFormik
            formik={step1}
            config={{
              component: "textinput",
              id: "tlfMobilNr",
              label: "Mobiltelefon",
              initialValue: "",
            }}
          />
        </div>

        {/* her starter warningboxen */}
        {personsWithSameName.length > 0 && (
          <DuplicatePersonWarning
            numberOfPersons={personsWithSameName.length}
            personsWithSameName={personsWithSameName}
            onCancel={() => {
              setModalOpen(false);
              step1.resetForm();
              step2.resetForm();
            }}
          />
        )}
      </div>
      {personsWithSameName.length === 0 && (
        <StickyFooter>
          <Button
            color="secondary"
            onClick={() => {
              setModalOpen(false);
              setIsKoenManuallyChanged(false);
              step1.resetForm();
              step2.resetForm();
            }}
          >
            Annuller
          </Button>

          <Button type="submit" color="primary">
            Næste
          </Button>
        </StickyFooter>
      )}
    </form>
  );
}
